import {
  Box,
  Grid,
  Typography,
  TypographyPropsVariantOverrides,
} from "@mui/material";
import { useEffect, useRef, useState } from "react";
import { TypographyVariant } from "@mui/material/styles/createTypography";
import { OverridableStringUnion } from "@mui/types";
import {
  Control,
  UseFormClearErrors,
  UseFormGetValues,
  UseFormReset,
  UseFormSetValue,
  UseFormUnregister,
  UseFormWatch,
} from "react-hook-form";

import { colors } from "../../../styles/colors";
import fontWeight from "../../../styles/mui/fontWeight";
import { FormInputText } from "../../../components/formComponents/FormInputText";
import { rules } from "../../../utils/validations/Validations";
import { FormInputBox } from "../styles/style";
import { FormInputRadio } from "../../../components/formComponents/FormInputRadio";
import AddressFormInputUseForm from "../../../components/formComponents/AddressFormInputUseForm";
import {
  agencyDetailsFormProps1,
  agencyDetailsFormProps2,
  homeHealthServiceFormProps,
  ReferralSourceType,
} from "../../../constants/ReferralDetails";
import OrderingPhysician from "./OrderingPhysician";
import { PhoneFaxExtInput } from "../../../components/formComponents/PhoneFaxExtInput";
import { PriorAuthReferralSourceMasterResponse } from "../../../models/Api/Master";
import { getList, getValue, length } from "../../../utils";
import { PatientDetail, TabData } from "../../../models/TabSessionStorage";
import { VerticalBar } from "../styles/OrderingPhysician";
import { InputProps } from "../../../components/formComponents/constant/FormComponents";
import { DropdownOptionKeys } from "../../../constants/FormComponents";
import { BlueCardRequest } from "../../../constants/MemberDetails";

export interface PropsFromState {
  referralSourceFacilityMaster: PriorAuthReferralSourceMasterResponse;
  control: Control<TabData, any>;
  clearErrors: UseFormClearErrors<TabData>;
  watch: UseFormWatch<TabData>;
  reset: UseFormReset<TabData>;
  getValues: UseFormGetValues<TabData>;
  setValue: UseFormSetValue<TabData>;
  unregister: UseFormUnregister<any>;
  patientDetail: PatientDetail
}

export interface PropsFromDispatch {
  getPriorAuthReferralSourceFacilityMaster: () => void;
}

type AllProps = PropsFromState & PropsFromDispatch;

const ReferralDetails: React.FC<AllProps> = ({
  referralSourceFacilityMaster,
  getPriorAuthReferralSourceFacilityMaster,
  control,
  clearErrors,
  watch,
  reset,
  getValues,
  setValue,
  unregister,
  patientDetail,
}) => {
  const [referralSourceMaster, setReferralSourceMaster] = useState<
    DropdownOptionKeys[]
  >([]);
  const [showReferralSourceForm, setShowReferralSourceForm] = useState(true);

  const homeHealthServiceSearchInput = useRef(null);
  const agencyDetailsSearchInput = useRef(null);
  const [isRequired, setIsRequired] = useState(false);
  useEffect(() => {
    renderReferralSourceForm(getValues("referralSource.facilityTypeId"));
  }, []);

  useEffect(() => {
    if (patientDetail.response === BlueCardRequest.IS_BLUE_CARD_REQUEST) { 
      setIsRequired(true);
  }
  }, [patientDetail])
  
  useEffect(() => {
    getPriorAuthReferralSourceFacilityMaster();
  }, []);

  useEffect(() => {
    const { unsubscribe } = watch((value, { name }) => {
      if (name === "referralSource.facilityTypeId") {
        renderReferralSourceForm(
          getValue(value, "referralSource.facilityTypeId")
        );
      }
    });
    return () => unsubscribe();
  }, [watch]);

  const renderReferralSourceForm = (facilityTypeId: string | undefined) => {
    if (facilityTypeId) {
      if (
        facilityTypeId === ReferralSourceType.Hospital ||
        facilityTypeId === ReferralSourceType.PostAcuteFacility
      ) {
        setShowReferralSourceForm(true);
      } else {
        setValue("referralSource.facilityName", null);
        setValue("referralSource.referralSourceName", null);
        setValue("referralSource.referralSourceNpi", null);
        setValue("referralSource.referralSourcePhoneNo", null);
        setValue("referralSource.referralSourceFax", null);
        setValue("referralSource.referralSourceExtension", null);
        setShowReferralSourceForm(false);
      }
    } else {
      setShowReferralSourceForm(true);
    }
  };
  const getAddressLabels = (agencyDetailsFormProps2: InputProps[], isRequired:boolean) => {
    if (!isRequired) {
      return agencyDetailsFormProps2.map((field: InputProps) => ({
        ...field,
        helper: field.helper.optional,
      }));
    }

    return agencyDetailsFormProps2.map((field: InputProps) => ({
      ...field,
      helper: field.helper.required,
    }));
  };

  useEffect(() => {
    const masterList = getValue(referralSourceFacilityMaster, "response", []);
    if (length(masterList)) {
      setReferralSourceMaster(
        getList(masterList, {
          label: "facilityTypeName",
          value: "facilityTypeId",
        })
      );
    }
  }, [referralSourceFacilityMaster]);

  const onFacilityTypeChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const selectedValue = event.target.value;
    const selectedOption = referralSourceMaster.find(
      (item: DropdownOptionKeys) => item.value == selectedValue
    );
    setValue(
      "referralSource.facilityTypeName",
      getValue(selectedOption, "label", "")
    );
  };

  const typographyBox = (
    variant:
      | OverridableStringUnion<
          "inherit" | TypographyVariant,
          TypographyPropsVariantOverrides
        >
      | undefined,
    value: string,
    color: string,
    fontWeight: string,
    sx?: object
  ) => {
    return (
      <Typography
        variant={variant}
        fontWeight={fontWeight}
        color={color}
        sx={sx}
      >
        {value}
      </Typography>
    );
  };

  const getValidationRule = (
    helper: any,
    isRequired: boolean
  ) => {
    if (!helper) return null;
    return isRequired ? helper.required : helper.optional;
  };

  return (
    <Box p={"1rem 2rem"}>
      <Grid container>
        <Grid size={{ xs: 12 }}>
          <Box>
            {typographyBox(
              "h6",
              "Referral Source Information",
              colors.fonts[20],
              fontWeight.Weight[5]
            )}
          </Box>
        </Grid>
        <Grid size={{ xs: 12 }}>
          <Box>
            <Grid container>
              <Grid size={{ xs: 12 }}>
                <Box pt={"1.1rem"}>
                  <FormInputRadio
                    control={control}
                    name={"referralSource.facilityTypeId"}
                    label={"Facility Type: "}
                    onInputChange={onFacilityTypeChange}
                    options={referralSourceMaster}
                    helper={rules.referralSourceFacilityTypeId}
                  />
                </Box>
              </Grid>
              {showReferralSourceForm ? (
                <>
                  <Grid size={{ xs: 3 }}>
                    <Box sx={FormInputBox}>
                      <FormInputText
                        control={control}
                        helper={rules.referralFacility}
                        name={"referralSource.facilityName"}
                        label="Referring Facility Name: "
                      />
                    </Box>
                  </Grid>
                  <Grid size={{ xs: 3 }}>
                    <Box sx={FormInputBox}>
                      <FormInputText
                        control={control}
                        helper={rules.npiRequired}
                        name={"referralSource.referralSourceNpi"}
                        label="NPI: "
                        textLength={10}
                      />
                    </Box>
                  </Grid>
                  <Grid size={{ xs: 3 }}>
                    <Box sx={FormInputBox}>
                      <PhoneFaxExtInput
                        label="Phone:"
                        control={control}
                        phoneFaxName={"referralSource.referralSourcePhoneNo"}
                        extName={"referralSource.referralSourceExtension"}
                        rules={rules.phone}
                      />
                    </Box>
                  </Grid>
                  <Grid size={{ xs: 3 }}>
                    <Box sx={FormInputBox}>
                      <PhoneFaxExtInput
                        label="Fax:"
                        control={control}
                        phoneFaxName={"referralSource.referralSourceFax"}
                        rules={rules.sendFax}
                        isFax
                      />
                    </Box>
                  </Grid>
                  <Grid size={{ xs: 3 }}>
                    <Box sx={FormInputBox}>
                      <FormInputText
                        control={control}
                        helper={rules.contactName}
                        name={"referralSource.referralSourceName"}
                        label="Contact Name: "
                      />
                    </Box>
                  </Grid>
                </>
              ) : null}
            </Grid>
          </Box>
        </Grid>
        <Grid size={{ xs: 12 }} mt={1}>
          <Box>
            <OrderingPhysician
              control={control}
              getValues={getValues}
              setValue={setValue}
              reset={reset}
              watch={watch}
              clearErrors={clearErrors}
              unregister={unregister}
            />
          </Box>
        </Grid>
        <Grid size={{ xs: 12 }}>
          <Box pt="1.7rem" pb="0.5rem">
            <Box component={"span"} display={"inline-flex"}>
              {typographyBox(
                "h6",
                "Home Health Service Address: ",
                colors.fonts[20],
                fontWeight.Weight[5]
              )}
            </Box>
            <Box component={"span"} display={"inline-flex"}>
              {typographyBox(
                "h6",
                "|",
                colors.fonts[20],
                fontWeight.Weight[5],
                VerticalBar
              )}
              {typographyBox(
                "subtitle1",
                "Please enter the address where the member will be receiving services.",
                colors.fonts[20],
                fontWeight.Weight[2]
              )}
            </Box>
          </Box>
          <Box>
            <AddressFormInputUseForm
              control={control}
              searchInput={homeHealthServiceSearchInput}
              reset={reset}
              getValues={getValues}
              setValue={setValue}
              labels={homeHealthServiceFormProps}
              addressLabel={""}
              addressVariant={undefined}
            />
          </Box>
        </Grid>
        <Grid size={{ xs: 12 }}>
          <Box pt="0.5rem" pb="0.5rem">
            <Box component={"span"} display={"inline-flex"}>
              {typographyBox(
                "h6",
                isRequired ? "Agency Details" : "Agency Details (Optional) ",
                colors.fonts[20],
                fontWeight.Weight[5]
              )}
            </Box>
            <Box component={"span"} display={"inline-flex"}>
              {typographyBox(
                "h6",
                "|",
                colors.fonts[20],
                fontWeight.Weight[5],
                VerticalBar
              )}
              {typographyBox(
                "subtitle1",
                "Please enter agency details if an agency is preferred by or for this member.",
                colors.fonts[20],
                fontWeight.Weight[2]
              )}
            </Box>
          </Box>
          <Box>
            <Grid container>
              {agencyDetailsFormProps1.map((label: InputProps) => {
                const validationRule = getValidationRule(label.helper, isRequired);
                if (label.name === "serviceProvider.phone") {
                  return (
                    <Grid size={{ xs: 3 }} key={label.name}>
                      <Box sx={FormInputBox}>
                        <PhoneFaxExtInput
                          label={label.label}
                          control={control}
                          phoneFaxName={label.name}
                          extName={label.extName}
                          rules={validationRule}
                        />
                      </Box>
                    </Grid>
                  );
                } else if (label.name === "serviceProvider.fax") {
                  return (
                    <Grid size={{ xs: 3 }} key={label.name}>
                      <Box sx={FormInputBox}>
                        <PhoneFaxExtInput
                          label={label.label}
                          control={control}
                          phoneFaxName={label.name}
                          rules={validationRule}
                          isFax
                        />
                      </Box>
                    </Grid>
                  );
                }
                return (
                  <Grid size={{ xs: 3 }} key={label.name}>
                    <Box sx={FormInputBox}>
                      <FormInputText
                        control={control}
                        helper={validationRule}
                        name={label.name}
                        label={label.label}
                      />
                    </Box>
                  </Grid>
                );
              })}
              {
                <AddressFormInputUseForm
                  control={control}
                  searchInput={agencyDetailsSearchInput}
                  reset={reset}
                  getValues={getValues}
                  setValue={setValue}
                  labels={getAddressLabels(agencyDetailsFormProps2, isRequired)}
                  addressLabel={""}
                  addressVariant={undefined}
                  agencyDetails={true}
                />
              }
            </Grid>
          </Box>
        </Grid>
      </Grid>
    </Box>
  );
};

export default ReferralDetails;
